<template>
  <div class="page-sales" v-loading="loading">
    <portal to="layout-nav-title">客户销售额统计</portal>

    <el-form inline>
      <el-form-item label="业务员" prop="user_id_eq">
        <app-read-options
          v-model="filter.user_id_eq"
          url="/users/read_options"
          :params="{ per_page: 100 }"
          label-field="screen_name"
        />
      </el-form-item>
      <el-form-item label="客户" prop="id_eq">
        <app-read-options
          v-model="filter.id_eq"
          url="/customers/read_options"
          label-field="name"
        />
      </el-form-item>
      <el-form-item label="数据统计维度" prop="finished_on_not_null">
        <el-select v-model="filter.finished_on_not_null" placeholder="请选择">
          <el-option label="下单" :value="undefined" />
          <el-option label="完结" :value="true" />
          <el-option label="待完结" :value="false" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleFilter">筛选</el-button>
        <el-button @click="handleReset">重置</el-button>
        <el-button @click="handleExport">导出</el-button>
      </el-form-item>
    </el-form>

    <div class="box-table" :key="$route.fullPath">
      <div class="table-header">
        <el-button
          icon="el-icon-arrow-left"
          circle
          size="mini"
          @click="handleChangeYear(year - 1)"
        ></el-button>
        <span class="title">{{ `${year}年客户销售统计` }}</span>
        <el-button
          :disabled="year >= currentYear"
          icon="el-icon-arrow-right"
          circle
          size="mini"
          @click="handleChangeYear(year + 1)"
        ></el-button>
      </div>
      <div class="table-content" v-if="tableData.length">
        <el-table
          :data="tableData"
          size="small"
          stripe
          border
          show-summary
          :summary-method="summaryMethod"
          height="100%"
          @sort-change="handleSortChange"
          :default-sort="tableSort"
        >
          <el-table-column
            prop="name"
            label=""
            :fixed="fixedColumn ? 'left' : undefined"
            min-width="120"
          />
          <el-table-column
            v-for="(month, index) in monthTableColumns"
            :prop="`month_${index + 1}`"
            :key="month"
            :label="month"
            min-width="100"
            sortable
          >
            <template #default="scope">
              <app-locale-string :value="scope.row[`month_${index + 1}`]" />
            </template>
          </el-table-column>
          <el-table-column
            label="总计"
            prop="total"
            :fixed="fixedColumn ? 'right' : undefined"
            min-width="100"
            class-name="cell-total"
            sortable
          >
            <template #default="scope">
              <app-locale-string :value="scope.row.total" />
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
    <br />
    <bean-pagination v-bind="pagination" @submit="handlePagination" />
  </div>
</template>

<script>
import { Vue, Component, Mixins, Watch } from 'vue-property-decorator';
import Utils from '@/components/utils.vue';
import moment from 'moment';
import { MONTH_NAMES } from '@/constants';
import _ from 'lodash';
import { localeString } from '@/components/locale-string';
import { screenService } from 'beans-admin-plugin';
import { exportFile } from '@/utils/export';

const SORT_ORDER_MAP = {
  ascending: 'asc',
  descending: 'desc',
};

const ELEMENT_SORT_ORDER_MAP = {
  asc: 'ascending',
  desc: 'descending',
};

@Component
export default class CustomerSalesAmounts extends Mixins(Vue, Utils) {
  currentYear = moment().year();
  year = moment().year();

  filter = {
    id_eq: undefined,
    user_id_eq: undefined,
    finished_on_not_null: undefined,
  };

  summary = {};
  data = [];
  pagination = {};
  loading = false;

  get summaryKeyByMonth() {
    return _.mapKeys(this.summary, (v, k) => {
      return Number(k.split('-')[1]);
    });
  }

  get summaryTotal() {
    return _.sumBy(Object.values(this.summary), Number);
  }

  get fixedColumn() {
    return screenService.isLg;
  }

  get monthTableColumns() {
    if (this.year === this.currentYear) {
      return MONTH_NAMES.slice(0, moment().month() + 1);
    }
    return MONTH_NAMES;
  }

  get tableData() {
    return this.data.map((item) => {
      const newItem = { id: item.id, name: item.name };
      _.range(1, 13).forEach((month) => {
        newItem[`month_${month}`] = 0;
      });
      _.forEach(item.datum, (v, date) => {
        const month = Number(date.split('-')[1]);
        newItem[`month_${month}`] = Number(v) || 0;
      });
      newItem.total = _.sumBy(Object.values(item.datum).map(Number));
      return newItem;
    });
  }

  get perPage() {
    return this.$route.query.per_page || undefined;
  }

  summaryMethod(param) {
    const { columns, data } = param;
    const sums = [];
    columns.forEach((column, index) => {
      if (index === 0) {
        sums[index] = '本页总计\n所有总计';
      } else {
        // 本页数据汇总
        const pageTotal = localeString({
          value: _.sumBy(data, (item) => Number(item[column.property])),
          showUnit: false,
        });
        // summary数据汇总
        let allTotal = '';
        if (column.property === 'total') {
          allTotal = localeString({
            value: this.summaryTotal,
            showUnit: false,
          });
        } else if (column.property.startsWith('month_')) {
          const columnMonth = Number(column.property.split('_')[1]);
          allTotal = localeString({
            value: _.get(this.summaryKeyByMonth, columnMonth, 0),
            showUnit: false,
          });
        }

        sums[index] = [pageTotal, allTotal].join('\n');
      }
    });
    return sums;
  }

  sortParams = {};
  get tableSort() {
    const sortConfig = {};
    const { sort_by, sort_order } = this.sortParams;

    if (sort_by && sort_order) {
      sortConfig[sort_by] = ELEMENT_SORT_ORDER_MAP[sort_order];
    }

    return sortConfig;
  }

  async fetchData(fetchSummary = true) {
    this.loading = true;
    try {
      const params = { ...this.$route.query, year: this.year };
      const [res1, res2] = await Promise.all([
        this.$request.get('/reports/customer_sales_amounts', {
          ...params,
          ...this.sortParams,
        }),
        fetchSummary
          ? this.$request.get('/reports/customer_sales_amounts/summary', params)
          : undefined,
      ]);
      this.data = res1.data;
      this.pagination = res1.pagination;
      if (fetchSummary) {
        this.summary = res2;
      }
    } finally {
      this.loading = false;
    }
  }

  handleSortChange(column) {
    const { prop, order } = column;
    if (order) {
      this.sortParams.sort_by = prop;
      this.sortParams.sort_order = SORT_ORDER_MAP[order];
    } else {
      delete this.sortParams.sort_by;
      delete this.sortParams.sort_order;
    }
    this.fetchData(false);
  }

  created() {
    Object.assign(
      this.filter,
      _.pick(this.$route.query, Object.keys(this.filter))
    );
    this.year = this.$route.query.year || this.currentYear;
  }

  mounted() {
    this.fetchData();
  }

  handlePagination({ page, size }) {
    this.$router.replace({
      query: { ...this.$route.query, page, per_page: size },
    });
  }

  handleChangeYear(year) {
    this.year = year;
    this.sortParams = {};
    this.$router.replace({
      query: { ...this.$route.query, year, page: 1, per_page: this.perPage },
    });
  }

  handleReset() {
    this.filter = {};
    this.sortParams = {};
    this.$router.replace({
      query: { year: this.year, page: 1, per_page: this.perPage },
    });
  }

  handleFilter() {
    this.sortParams = {};
    this.$router.replace({
      query: {
        ...this.filter,
        year: this.year,
        page: 1,
        per_page: this.perPage,
      },
    });
  }

  @Watch('$route.query')
  onQueryChange() {
    this.fetchData();
  }

  async handleExport() {
    this.exportLoading = true;
    try {
      const params = { ...this.$route.query, year: this.year };
      await exportFile('reports/customer_sales_amounts/export', params);
    } finally {
      this.exportLoading = false;
    }
  }
}

export const route = [
  {
    path: '/reports/customer_sales_amounts',
    name: 'reports/customer_sales_amounts.index',
    component: CustomerSalesAmounts,
    meta: {
      can: 'reports/customer_sales_amounts.read',
    },
  },
];
</script>

<style lang="scss" scoped>
.page-sales {
  height: 100%;
  box-sizing: border-box;
  padding: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.box-table {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  .table-header {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: $primary;
    color: #fff;
    padding: 5px 10px;

    .title {
      margin: 0 10px;
    }
  }

  .table-content {
    flex: 1;
    overflow: hidden;

    /deep/ .cell-total {
      color: $primary;
      font-weight: 500;
    }

    /deep/ .el-table__footer {
      td {
        color: $primary;
        font-weight: 500;

        .cell {
          white-space: pre-wrap;
        }
      }
    }
  }
}
</style>
